import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AppDialog } from './AppDialog'
import type {
  AlertOptions,
  ConfirmOptions,
  DialogApi,
  DialogRequest,
  PromptOptions,
  SelectOptions,
} from './dialog-types'
import { DialogContext } from './useDialog'

export function DialogProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<DialogRequest[]>([])
  const queueRef = useRef<DialogRequest[]>([])
  const nextId = useRef(1)

  useEffect(() => {
    queueRef.current = queue
  }, [queue])

  useEffect(
    () => () => {
      for (const request of queueRef.current) {
        if (request.kind === 'alert') request.resolve()
        else if (request.kind === 'confirm') request.resolve(false)
        else request.resolve(null)
      }
    },
    []
  )

  const enqueue = useCallback((request: DialogRequest) => {
    setQueue((current) => [...current, request])
  }, [])

  const api = useMemo<DialogApi>(
    () => ({
      alert: (options: AlertOptions) =>
        new Promise<void>((resolve) => {
          enqueue({
            id: nextId.current++,
            kind: 'alert',
            options,
            resolve,
            returnFocus: document.activeElement as HTMLElement | null,
          })
        }),
      confirm: (options: ConfirmOptions) =>
        new Promise<boolean>((resolve) => {
          enqueue({
            id: nextId.current++,
            kind: 'confirm',
            options,
            resolve,
            returnFocus: document.activeElement as HTMLElement | null,
          })
        }),
      prompt: (options: PromptOptions) =>
        new Promise<string | null>((resolve) => {
          enqueue({
            id: nextId.current++,
            kind: 'prompt',
            options,
            resolve,
            returnFocus: document.activeElement as HTMLElement | null,
          })
        }),
      select: (options: SelectOptions) =>
        new Promise<string | null>((resolve) => {
          enqueue({
            id: nextId.current++,
            kind: 'select',
            options,
            resolve,
            returnFocus: document.activeElement as HTMLElement | null,
          })
        }),
    }),
    [enqueue]
  )

  const active = queue[0]
  const finish = useCallback((value?: string | boolean) => {
    setQueue((current) => {
      const [request, ...rest] = current
      if (!request) return current
      if (request.kind === 'alert') request.resolve()
      else if (request.kind === 'confirm') request.resolve(value === true)
      else request.resolve(typeof value === 'string' ? value : null)
      queueMicrotask(() => request.returnFocus?.focus())
      return rest
    })
  }, [])

  return (
    <DialogContext.Provider value={api}>
      {children}
      {active && (
        <AppDialog
          key={active.id}
          request={active}
          onCancel={() => finish()}
          onSubmit={(value) => finish(value ?? true)}
        />
      )}
    </DialogContext.Provider>
  )
}
